import { Badge, Group, Title, Tooltip } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { SplitButton } from "shared/components/global/SplitButton";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    composeFormStateToDraftInfoInput,
    createComposeFormState,
    useComposeForm,
} from "shared/components/message/compose/context";
import { useComposeFormPermissionChecker } from "shared/components/message/compose/useComposeFormPermissionChecker";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { TemplateSearchRepository } from "shared/components/message/templates/data";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { logger } from "shared/utils/logger";
import { some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { container } from "tsyringe";

const Component = () => {
    const { id } = useParams({ from: "/_auth/messages/templates/$id" });
    const { data } = useSuspenseQuery(queries.template(id));
    const nav = useNavigate();

    const form = useComposeForm({
        initialValues: createComposeFormState(data.message),
        validate: zodResolver(composeFormStateSchema),
    });
    const permissionChecker = useComposeFormPermissionChecker(form);
    const actions = useMessageActions();

    const repo = container.resolve(TemplateSearchRepository);

    const saveTemplate = useGraphqlMutation(mutations.save);

    function handleSave() {
        const validation = form.validate();
        if (validation.hasErrors) {
            logger.warn(validation.errors);
            return;
        }

        some(form.getValues())
            .map(composeFormStateToDraftInfoInput.parse)
            .take((params) => saveTemplate.mutateAsync({ id: data.message.id, params }))
            .then((d) => repo.upsert({ ...data, message: d.draft }))
            .then(() => qk.invalidate("templates"))
            .then(() => {
                form.resetDirty();
                notify.show.success("Template Saved");
            })
            .then(() => nav({ to: "/messages/templates" }))
            .catch(notify.catch);
    }

    useDirtyFormBlocker(form);

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>Edit Message</Title>
                <Badge color="blue">Template</Badge>
            </Content.Heading>

            <ComposeFormProvider form={form}>
                <ComposeForm />
            </ComposeFormProvider>

            <Group justify="flex-end">
                <Tooltip {...permissionChecker.getTooltipProps()}>
                    <SplitButton
                        onClick={handleSave}
                        menu={{ "Send Preview": { fn: () => actions.openPreviewModal({ form }) } }}
                        disabled={permissionChecker.hasErrors}
                        loading={saveTemplate.isPending}
                    >
                        Save Template
                    </SplitButton>
                </Tooltip>
            </Group>
        </Content>
    );
};

const queries = {
    template: (id: string) =>
        queryOptions({
            queryKey: qk("templates", "edit", id),
            select: ({ template }) => template,
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query TemplateEdit($id: UUID!) {
                            template(id: $id) {
                                ...TemplateLike
                                message {
                                    id
                                    ...ComposeDetails
                                }
                            }
                        }
                    `),
                    { id },
                ),
        }),
};

const mutations = {
    save: graphql(`
        mutation TemplateEditSave($id: UUID!, $params: DraftInfoInput!) {
            draft: updateDraft(id: $id, params: $params) {
                id
                subject
                severity
                richBody
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/templates/$id")({
    component: Component,
});
